////////////////////////////////////////
// # GOLDEN RULE!!!! HONOR ABOVE ALL!!!!
////////////////////////////////////////
// NO NEW FILES!!!!!!!!!
////////////////////////////////////////

// I. FILE HEADER [WHITE #FFFFFF]
// ⚪ Socratic AI Interviewer Pea for Taytrom Publishing Co - Pure presentation component (Masterpiece Chat UI)

// III. IMPORTS [GREEN #00FF00]
// 🟢 Third-party dependencies
import { memo, RefObject } from 'react';
import styles from './TaytromPublishingCo_Kings_Home_SRC_SocraticAIInterviewer_Lobby_Pea.module.css';

// IV. INDEPENDENT CONSTANTS [TEAL #00FFFF]
// 🔵 Component props interface
interface ChatMessage {
  messageId: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
  phase: number;
}

interface InterviewPhase {
  phaseNumber: number;
  title: string;
  icon: string;
}

interface PeaSocraticAiInterviewerProps {
  messages: ChatMessage[];
  inputValue: string;
  isLoading: boolean;
  isAiTyping: boolean;
  currentPhase: number;
  phases: InterviewPhase[];
  error: string | null;
  messagesEndRef: RefObject<HTMLDivElement>;
  inputRef: RefObject<HTMLTextAreaElement>;
  questionsAnswered: number;
  timeInvested: string;
  followUpSuggestions: string[];
  editingMessageId: string | null;
  editingContent: string;
  onSendMessage: () => void;
  onInputChange: (value: string) => void;
  onKeyPress: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  onFollowUpClick: (followUp: string) => void;
  onExportClick: () => void;
  onPhaseClick: (phaseNumber: number) => void;
  onRetryMessage: () => void;
  onDismissError: () => void;
  onStartEditMessage: (messageId: string, content: string) => void;
  onCancelEditMessage: () => void;
  onSaveEditMessage: () => void;
  onEditingContentChange: (content: string) => void;
  onDeleteMessage: (messageId: string) => void;
  className?: string;
}

// V. FUNCTION/CLASS NAME [BLUE #0000FF] - OUTPUT DECLARATION
// 🔵 Socratic AI Interviewer Pea Component (Institutional Analysis Instrument)
const Pea_SocraticAiInterviewer: FC<PeaSocraticAiInterviewerProps> = ({
  messages,
  inputValue,
  isLoading,
  isAiTyping,
  currentPhase,
  phases,
  error,
  messagesEndRef,
  inputRef,
  questionsAnswered,
  timeInvested,
  followUpSuggestions,
  editingMessageId,
  editingContent,
  onSendMessage,
  onInputChange,
  onKeyPress,
  onFollowUpClick,
  onExportClick,
  onPhaseClick,
  onRetryMessage: _onRetryMessage,
  onDismissError: _onDismissError,
  onStartEditMessage,
  onCancelEditMessage,
  onSaveEditMessage,
  onEditingContentChange,
  onDeleteMessage,
  className
}) => {
  // 🔵 Helper function to format timestamp
  const formatTime = (date: Date): string => {
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  // 🔵 Phase Latin epigraphs (scholarly aesthetic)
  const phaseEpigraphs = [
    'Ex Origine Veritas', // From Origin, Truth
    'Ars Est Celendum Artem', // Art Is To Conceal Art
    'Inter Arma Silent Leges', // In War, Laws Are Silent (competitive positioning)
    'Astra Inclinant, Non Necessitant', // Stars Incline, They Do Not Compel (vision)
    'Sapere Aude' // Dare To Know
  ];

  // X. RENDER/EXPORTS [BLACK #000000] - OUTPUT DELIVERY
  return (
    <div
      className={`${styles.institutionalContainer} ${className || ''}`}
      role="application"
      aria-label="Cicero AI Interview System"
    >
      {/* Toast Notifications - Dark Theme with TPC Branding */}
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: 'linear-gradient(135deg, rgba(30,30,30,0.95) 0%, rgba(20,20,20,0.85) 100%)',
            backdropFilter: 'blur(20px)',
            color: '#ffffff',
            border: '1px solid rgba(156,39,176,0.3)',
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(156,39,176,0.12), inset 0 1px 0 rgba(255,255,255,0.05)',
            padding: '12px 16px',
            fontSize: '14px',
            fontWeight: 500,
            fontFamily: "'Inter', -apple-system, sans-serif",
            letterSpacing: '-0.2px',
          },
          success: {
            iconTheme: {
              primary: '#4caf50',
              secondary: '#0a0a0a',
            },
          },
          error: {
            iconTheme: {
              primary: '#f44336',
              secondary: '#0a0a0a',
            },
          },
        }}
      />

      {/* Screen reader announcement for keyboard shortcuts */}
      <div className="sr-only" aria-live="polite" aria-atomic="true">
        Keyboard shortcuts: Ctrl+K to focus input, Ctrl+E to export, Ctrl+1-5 to navigate phases, Escape to clear input
      </div>

      {/* Header */}
      <header className={styles.institutionalHeader} role="banner">
        <div className={styles.headerTop}>
          <div className={styles.logoContainer}>
            <div className={styles.logoMark}>TPC</div>
            <div className={styles.logoText}>
              <div className={styles.logoCompany}>TAYLOR PUBLISHING CO.</div>
              <div className={styles.logoTagline}>EST. 2025</div>
            </div>
          </div>
          <div className={styles.headerTitle}>CICERO INTERVIEW INTERFACE</div>
        </div>
      </header>

      {/* Three-Column Layout */}
      <div className={styles.threeColumnLayout}>
        {/* LEFT COLUMN: Phase Navigation */}
        <aside
          className={styles.leftColumn}
          role="complementary"
          aria-label="Interview phases navigation"
        >
          <div className={styles.phaseNavigationTitle}>INTERVIEW PHASES</div>
          <div className={styles.phaseNavigationDivider}></div>

          <nav
            className={styles.verticalPhaseTracker}
            role="navigation"
            aria-label="Interview phases"
          >
            {phases.map((phase, index) => (
              <button
                key={phase.phaseNumber}
                className={`${styles.phaseTrackerItem} ${
                  phase.phaseNumber === currentPhase ? styles.phaseTrackerActive : ''
                } ${
                  phase.phaseNumber < currentPhase ? styles.phaseTrackerCompleted : ''
                }`}
                onClick={() => onPhaseClick(phase.phaseNumber)}
                aria-label={`Phase ${phase.phaseNumber}: ${phase.title}`}
                aria-current={phase.phaseNumber === currentPhase ? 'step' : undefined}
                tabIndex={0}
              >
                <div className={styles.phaseTrackerNumber} aria-hidden="true">{phase.phaseNumber}</div>
                <div className={styles.phaseTrackerContent}>
                  <div className={styles.phaseTrackerTitle}>{phase.title}</div>
                  <div className={styles.phaseTrackerEpigraph} aria-hidden="true">{phaseEpigraphs[index]}</div>
                </div>
              </button>
            ))}
          </nav>

          <div className={styles.methodologyLink}>
            <button className={styles.methodologyButton}>
              About Cicero's Methodology →
            </button>
          </div>
        </aside>

        {/* CENTER COLUMN: Conversation Card */}
        <main className={styles.centerColumn}>
          <div className={styles.conversationCard}>
            {/* Watermark */}
            <div className={styles.watermark}>TAYLOR PUBLISHING CO. • CICERO</div>

            {/* Messages */}
            <div className={styles.conversationMessages}>
              <div className={styles.messagesList}>
                {messages.map((message) => {
                  const isEditing = editingMessageId === message.messageId;
                  const isUserMessage = message.role === 'user';

                  return (
                    <div
                      key={message.messageId}
                      data-message-id={message.messageId}
                      className={`${styles.messageWrapper} ${
                        isUserMessage ? styles.userMessageWrapper : styles.aiMessageWrapper
                      }`}
                    >
                      <div className={styles.messageRole}>
                        {isUserMessage ? 'RESPONDENT' : 'CICERO'}
                      </div>
                      <div
                        className={`${styles.messageBubble} ${
                          isUserMessage ? styles.userMessage : styles.aiMessage
                        }`}
                      >
                        {isEditing ? (
                          // Edit mode UI
                          <div className={styles.editingContainer}>
                            <textarea
                              className={styles.editingTextarea}
                              value={editingContent}
                              onChange={(e) => onEditingContentChange(e.target.value)}
                              rows={3}
                              autoFocus
                            />
                            <div className={styles.editingActions}>
                              <button
                                className={styles.saveEditButton}
                                onClick={onSaveEditMessage}
                                disabled={!editingContent.trim()}
                              >
                                SAVE
                              </button>
                              <button
                                className={styles.cancelEditButton}
                                onClick={onCancelEditMessage}
                              >
                                CANCEL
                              </button>
                            </div>
                          </div>
                        ) : (
                          // Normal display mode
                          <>
                            <div className={styles.messageContent}>
                              <ReactMarkdown>{message.content}</ReactMarkdown>
                            </div>
                            <div className={styles.messageTimestamp}>
                              {formatTime(message.timestamp)}
                            </div>
                            {isUserMessage && (
                              <div className={styles.messageActions}>
                                <button
                                  className={styles.editMessageButton}
                                  onClick={() => onStartEditMessage(message.messageId, message.content)}
                                  title="Edit message"
                                >
                                  Edit
                                </button>
                                <button
                                  className={styles.deleteMessageButton}
                                  onClick={() => onDeleteMessage(message.messageId)}
                                  title="Delete message"
                                >
                                  Delete
                                </button>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}

                {/* Typing Indicator */}
                {isAiTyping && (
                  <div className={`${styles.messageWrapper} ${styles.aiMessageWrapper}`}>
                    <div className={styles.messageRole}>CICERO</div>
                    <div className={`${styles.messageBubble} ${styles.aiMessage}`}>
                      <div className={styles.typingIndicator}>
                        <span></span>
                        <span></span>
                        <span></span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Scroll anchor */}
                <div ref={messagesEndRef}></div>
              </div>
            </div>

            {/* Error Display */}
            {error && (
              <div className={styles.errorMessage}>
                <strong>ERROR:</strong> {error}
              </div>
            )}

            {/* Follow-Up Suggestions */}
            {followUpSuggestions.length > 0 && !isLoading && (
              <div className={styles.followUpContainer}>
                <div className={styles.followUpLabel}>SUGGESTED FOLLOW-UPS</div>
                <div className={styles.followUpDivider}></div>
                <div className={styles.followUpList}>
                  {followUpSuggestions.map((suggestion, index) => (
                    <button
                      key={index}
                      className={styles.followUpItem}
                      onClick={() => onFollowUpClick(suggestion)}
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Input Area */}
            <div className={styles.inputArea}>
              <div className={styles.inputLabel}>YOUR RESPONSE</div>
              <textarea
                ref={inputRef}
                className={styles.messageInput}
                placeholder="Respond thoughtfully…"
                value={inputValue}
                onChange={(e) => onInputChange(e.target.value)}
                onKeyPress={onKeyPress}
                disabled={isLoading}
                rows={4}
              />
              <div className={styles.inputFooter}>
                <div className={styles.inputHint}>
                  Press Enter to submit • Shift+Enter for new line
                </div>
                <button
                  className={styles.submitButton}
                  onClick={onSendMessage}
                  disabled={isLoading || !inputValue.trim()}
                >
                  {isLoading ? 'SUBMITTING...' : 'SUBMIT RESPONSE'}
                </button>
              </div>
            </div>
          </div>
        </main>

        {/* RIGHT COLUMN: Meta Panel */}
        <aside className={styles.rightColumn}>
          <div className={styles.metaPanelTitle}>CICERO SESSION</div>
          <div className={styles.metaPanelDivider}></div>

          <div className={styles.metadataGrid}>
            <div className={styles.metadataItem}>
              <div className={styles.metadataLabel}>Session</div>
              <div className={styles.metadataValue}>Interview #1</div>
            </div>
            <div className={styles.metadataItem}>
              <div className={styles.metadataLabel}>Current Phase</div>
              <div className={styles.metadataValue}>{currentPhase} of 5</div>
            </div>
            <div className={styles.metadataItem}>
              <div className={styles.metadataLabel}>Questions Answered</div>
              <div className={styles.metadataValue}>{questionsAnswered}</div>
            </div>
            <div className={styles.metadataItem}>
              <div className={styles.metadataLabel}>Session Duration</div>
              <div className={styles.metadataValue}>{timeInvested}</div>
            </div>
            <div className={styles.metadataItem}>
              <div className={styles.metadataLabel}>Status</div>
              <div className={styles.metadataValue}>
                {isLoading ? 'Processing...' : 'Active'}
              </div>
            </div>
          </div>

          <div className={styles.metaPanelDivider}></div>

          <button
            className={styles.exportTranscriptButton}
            onClick={onExportClick}
            title="Download Cicero interview transcript"
          >
            DOWNLOAD CICERO TRANSCRIPT
          </button>

          <div className={styles.autosaveIndicator}>
            Auto-saved • {new Date().toLocaleTimeString('en-US', {
              hour: '2-digit',
              minute: '2-digit',
              hour12: false
            })}
          </div>
        </aside>
      </div>

      {/* Footer */}
      <footer className={styles.institutionalFooter}>
        <div className={styles.footerLeft}>
          <span>© 2025 Taytrom Publishing Co.</span>
          <span className={styles.footerDot}>•</span>
          <span>All Rights Reserved</span>
        </div>
        <div className={styles.footerCenter}>
          <em>Veritas per Analysin</em>
        </div>
        <div className={styles.footerRight}>
          Cicero Interview System v1.0
        </div>
      </footer>
    </div>
  );
};

// Memoize Pea component to prevent unnecessary re-renders
// Only re-render when props actually change (performance optimization)
export default memo(Pea_SocraticAiInterviewer);
